/* eslint-disable array-callback-return */
import { useMount, useRequest, useSetState } from "ahooks";
import InputLabel from "../../../../../base/basicComponents/inputLabel";
import NormalSelect, { NormalSelectV5 } from "base/basicComponents/select";
import Buttons from "../../../../../base/basicComponents/button";
import procedureListService from "../../../../../service/procedureList";
import TopFilterBase from "../../../../commonComponents/listBase/TopFilterBase";
import styles from "./index.module.scss";
import "../../../../../assets/css/style.css";
import { FilterOptionList, PropsType, SelectOptions } from "./types";
import { GetGlobalFiltersResponseType } from "service/procedureList/types";
import { useRef } from "react";
import { isArray } from "lodash-es";

const statusList = [
  { name: "Active", value: "1" },
  { name: "Inactive", value: "0" },
  { name: "Both", value: "2" },
] as const;
const { sortList, handleChange, handleDeSelect, handleFilterOption, handleFilterOptionV5 } =
  TopFilterBase();

export default function TopFilter(props: PropsType) {
  const { handleFilterClick, handleClear } = props;

  // @ts-ignore
  const totalFilterDataRef = useRef<GetGlobalFiltersResponseType | null>();

  /* state about selects' data */
  const [filterOptionList, setFilterOptionList] = useSetState<FilterOptionList>({
    accountAbbr: [],
    taskGroup: [],
    category: [],
  });

  /* state about select's selected data */
  const [selectOptions, setSelectOptions] = useSetState<SelectOptions>({
    accountAbbr: [],
    accountStatus: ["1"],
    taskGroup: [],
    category: [],
    taskStatus: ["1"],
  });

  const handleAccountStatusChange = (status: string) => {
    setSelectOptions({
      accountStatus: [status],
    });
    runGetTopFilterData(status !== statusList[2].value ? { isActive: status } : undefined);
  };

  const handleClearClick = () => {
    setFilterOptionList({
      taskGroup: [],
      category: [],
    });
    setSelectOptions({
      accountAbbr: [],
      taskGroup: [],
      category: [],
      accountStatus: ["1"],
      taskStatus: ["1"],
    });
    handleClear();
  };

  const handleAccountCodeChange = (accountAbbrList: number[]) => {
    const optionList = filterOptionList.accountAbbr;
    const { currentFilterOptionList, currentSelectOptions } = convertSourceData(accountAbbrList);

    setSelectOptions(currentSelectOptions);
    setFilterOptionList({
      ...currentFilterOptionList,
      accountAbbr: [
        ...handleChange(optionList, accountAbbrList),
        ...optionList!.sort((s, t) => sortList(s, t)),
      ],
    });
  };

  const handleTaskGroupChange = (taskGroupList: string[]) => {
    const { currentFilterOptionList, currentSelectOptions } = convertSourceData(
      selectOptions.accountAbbr,
      taskGroupList.flatMap((item) => item.split(",").map(Number))
    );
    const optionList = filterOptionList.taskGroup ?? [];
    setSelectOptions({
      taskGroup: taskGroupList,
      category: currentSelectOptions.category,
    });
    setFilterOptionList({
      taskGroup: [
        ...handleChange(optionList, taskGroupList),
        ...optionList.sort((s, t) => sortList(s, t)),
      ],
      category: currentFilterOptionList.category,
    });
  };

  const handleSelectAll = (filterName: keyof FilterOptionList) => {
    const optionList = filterOptionList[filterName];
    const allOptionsValue = optionList!.map((item) => item.value);
    setSelectOptions((prevState) => ({
      ...prevState,
      [filterName]: allOptionsValue,
    }));
    setFilterOptionList((prevState) => ({
      ...prevState,
      [filterName]: [...handleChange(optionList, allOptionsValue)],
    }));
    filterName === "accountAbbr" && handleAccountCodeChange(allOptionsValue as number[]);
    filterName === "taskGroup" && handleTaskGroupChange(allOptionsValue as string[]);
  };

  const handleDeselectAll = (filterName: keyof FilterOptionList) => {
    const optionList = filterOptionList[filterName];
    setSelectOptions((prevState) => ({
      ...prevState,
      [filterName]: [],
    }));
    setFilterOptionList((prevState) => ({
      ...prevState,
      [filterName]: [...optionList!.sort((s, t) => sortList(s, t))],
    }));
    filterName === "accountAbbr" && handleAccountCodeChange([]);
    filterName === "taskGroup" && handleTaskGroupChange([]);
  };

  const { run: runGetTopFilterData } = useRequest(procedureListService.getGlobalFilters, {
    manual: true,
    onSuccess: (data) => {
      const isFirstLoad = !totalFilterDataRef.current;
      totalFilterDataRef.current = data;
      // if (data.length === 0) return;
      const currentAccount =
        data.length > 0 &&
        data
          .map((i) => ({
            name: i.accountAbbr,
            value: i.accountId,
            ...i,
          }))
          .sort(sortList)[0];
      const { currentFilterOptionList, currentSelectOptions } = convertSourceData(
        currentAccount ? [currentAccount.accountId] : []
      );
      setSelectOptions(currentSelectOptions);
      setFilterOptionList(currentFilterOptionList);
      isFirstLoad && handleFilterClick(currentFilterOptionList, currentSelectOptions);
    },
  });

  const convertSourceData = (
    currentSelectedAccount: number[] = [],
    currentSelectedTaskGroupIds?: number[] | undefined
  ) => {
    let selectedTaskGroups = totalFilterDataRef
      .current!.filter((i) => currentSelectedAccount.includes(i.accountId))
      .flatMap((i) => i.taskGroups);
    if (isArray(currentSelectedTaskGroupIds)) {
      selectedTaskGroups = selectedTaskGroups.filter((tg) =>
        currentSelectedTaskGroupIds.includes(tg.taskGroupId)
      );
    }
    selectedTaskGroups = selectedTaskGroups.filter((i) => i.taskGroupId !== null);

    const selectedCategories = selectedTaskGroups
      .flatMap((tg) => tg.categories)
      .filter((i) => i.categoryId !== null);

    const taskGroupMap = new Map<string, number[]>();
    selectedTaskGroups.forEach((tg) => {
      if (!taskGroupMap.has(tg.taskGroupName)) {
        taskGroupMap.set(tg.taskGroupName, []);
      }
      taskGroupMap.get(tg.taskGroupName)!.push(tg.taskGroupId);
    });
    const taskGroupOptionList = Array.from(taskGroupMap.entries()).map(([name, value]) => ({
      name,
      value: value.join(","),
    }));

    const categoryMap = new Map<string, number[]>();
    selectedCategories.forEach((c) => {
      if (!categoryMap.has(c.path)) {
        categoryMap.set(c.path, []);
      }
      categoryMap.get(c.path)!.push(c.categoryId);
    });
    const categoryOptionList = Array.from(categoryMap.entries()).map(([name, value]) => ({
      name,
      value: value.join(","),
    }));

    const currentSelectOptions: SelectOptions = {
      ...selectOptions,
      accountAbbr: currentSelectedAccount,
      taskGroup: taskGroupOptionList
        .sort(sortList)
        .flatMap((i) => i.value)
        .map((i) => i),
      category: categoryOptionList
        .sort(sortList)
        .flatMap((i) => i.value)
        .map((i) => i),
    };

    const currentFilterOptionList = {
      accountAbbr: totalFilterDataRef
        .current!.map((i) => ({
          name: i.accountAbbr,
          value: i.accountId,
        }))
        .sort(sortList),
      taskGroup: taskGroupOptionList.sort(sortList),
      category: categoryOptionList.sort(sortList),
    };

    return { currentFilterOptionList, currentSelectOptions };
  };

  useMount(() => {
    runGetTopFilterData({ isActive: selectOptions.accountStatus![0] });
  });

  return (
    <div className={styles.topFilterContainer}>
      <div className={styles.topFilterSelects}>
        {props.isOpex && (
          <div className={styles.topFilterSelectItem}>
            <InputLabel text="Account Status" className={`${styles.topFilterSelectLabel} fs-14`} />
            <NormalSelect
              value={selectOptions.accountStatus}
              optionList={statusList}
              className="topFilterSelectInput"
              showArrow={true}
              maxTagCount="responsive"
              handleChange={handleAccountStatusChange}
              testid="procedure-list-account-status-select"
            />
          </div>
        )}
        <div className={styles.topFilterSelectItem}>
          <InputLabel text="Account Code" className={`${styles.topFilterSelectLabel} fs-14`} />
          <NormalSelect
            value={selectOptions.accountAbbr}
            optionList={filterOptionList.accountAbbr}
            className="topFilterSelectInput"
            mode="multiple"
            showArrow={true}
            maxTagCount="responsive"
            handleChange={handleAccountCodeChange}
            handleDeSelect={(e: any) => {
              const selectList = [...(selectOptions.accountAbbr ?? [])];
              setSelectOptions({ accountAbbr: handleDeSelect(e, selectList) });
            }}
            handleFilterOption={(input: any, option: any) => handleFilterOption(input, option)}
            isNeedSelectAll={(filterOptionList.accountAbbr ?? []).length > 0}
            handleSelectAll={() => handleSelectAll("accountAbbr")}
            handleDeselectAll={() => handleDeselectAll("accountAbbr")}
            testid="procedure-list-account-code-select"
          />
        </div>
        <div className={styles.topFilterSelectItem}>
          <InputLabel text="Task Group" className={`${styles.topFilterSelectLabel} fs-14`} />
          <NormalSelectV5
            value={selectOptions.taskGroup}
            optionList={filterOptionList.taskGroup ?? []}
            className="topFilterSelectInput"
            mode="multiple"
            showArrow={true}
            maxTagCount="responsive"
            handleChange={handleTaskGroupChange}
            handleDeSelect={(e) => {
              const selectList = [...(selectOptions.taskGroup ?? [])];
              setSelectOptions({ taskGroup: handleDeSelect(e, selectList) });
            }}
            handleFilterOption={(input, option) => handleFilterOptionV5(input, option)}
            isNeedSelectAll={(filterOptionList.taskGroup ?? []).length > 0}
            handleSelectAll={() => handleSelectAll("taskGroup")}
            handleDeselectAll={() => handleDeselectAll("taskGroup")}
            testid="procedure-list-task-group-select"
          />
        </div>
        <div className={styles.topFilterSelectItem}>
          <InputLabel text="Category" className={`${styles.topFilterSelectLabel} fs-14`} />
          <NormalSelectV5
            value={selectOptions.category}
            optionList={filterOptionList.category}
            className="topFilterSelectInput"
            mode="multiple"
            showArrow={true}
            maxTagCount="responsive"
            handleChange={(e: string[]) => {
              const optionList = filterOptionList.category ?? [];
              setSelectOptions({
                category: e,
              });
              setFilterOptionList({
                category: [
                  ...handleChange(optionList, e),
                  ...optionList.sort((s, t) => sortList(s, t)),
                ],
              });
            }}
            handleDeSelect={(e) => {
              const selectList = [...(selectOptions.category || [])];
              setSelectOptions({ category: handleDeSelect(e, selectList) });
            }}
            handleFilterOption={(input, option) => handleFilterOptionV5(input, option)}
            isNeedSelectAll={(filterOptionList.category ?? []).length > 0}
            handleSelectAll={() => handleSelectAll("category")}
            handleDeselectAll={() => handleDeselectAll("category")}
            testid="procedure-list-category-select"
          />
        </div>
        <div className={styles.topFilterSelectItem}>
          <InputLabel text="Task Status" className={`${styles.topFilterSelectLabel} fs-14`} />
          <NormalSelect
            value={selectOptions.taskStatus}
            optionList={statusList}
            className="topFilterSelectInput"
            showArrow={true}
            maxTagCount="responsive"
            handleChange={(e: string) => {
              setSelectOptions({
                taskStatus: [e],
              });
            }}
            testid="procedure-list-task-status-select"
          />
        </div>
      </div>
      <div className={styles.topFilterBtns}>
        <Buttons
          color="blue"
          size="middle"
          onClick={() => {
            handleFilterClick(filterOptionList, selectOptions);
          }}
          testid="procedure-list-filter-btn"
        >
          Filter
        </Buttons>
        <Buttons
          color="grey"
          size="large"
          onClick={handleClearClick}
          testid="procedure-list-clear-btn"
        >
          Clear Query
        </Buttons>
      </div>
    </div>
  );
}
